import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Zap, Image, Shield, QrCode, ArrowRight, RefreshCw } from 'lucide-react'
import { accountSignIn, isAccountLoggedIn } from '../accountAuth'
import { Button, Card, Input, Alert } from './ui'

const QrMockup = () => (
  <div className="relative animate-float" style={{ width: '280px', height: '320px' }}>
    {/* Ambient glow layers */}
    <div className="absolute inset-0 rounded-[2.5rem] blur-3xl scale-110"
      style={{ background: 'radial-gradient(ellipse at 40% 40%, rgba(37,99,235,0.35) 0%, rgba(14,165,183,0.2) 60%, transparent 100%)' }} />

    {/* Main card — 3D tilted */}
    <div
      className="brand-panel relative rounded-[2.5rem] p-6 flex flex-col items-center justify-center gap-5 h-full"
      style={{
        transform: 'perspective(900px) rotateX(6deg) rotateY(-10deg) rotateZ(-1deg)',
        boxShadow: '28px 36px 72px -8px rgba(37,99,235,0.25), 0 0 0 1px rgba(255,255,255,0.08) inset',
      }}
    >
      {/* YOWO wordmark */}
      <span
        className="text-4xl font-black tracking-wide"
        style={{
          fontFamily: 'Syne, sans-serif',
          background: 'linear-gradient(125deg, #2563eb 0%, #0ea5b7 60%, #06b6d4 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '0.06em',
        }}
      >
        YOWO
      </span>

      {/* QR dot grid */}
      <div className="grid grid-cols-5 gap-1.5">
        {Array.from({ length: 25 }).map((_, i) => {
          const isOn = [0,1,2,3,4,5,9,10,14,15,19,20,21,22,23,24,7,12,17,6,11,13,16,18].includes(i)
          return (
            <div
              key={i}
              className={`w-[14px] h-[14px] rounded-[3px] transition-opacity duration-300 ${
                isOn
                  ? 'bg-slate-900 dark:bg-slate-50'
                  : 'bg-slate-200/70 dark:bg-slate-700/60'
              }`}
            />
          )
        })}
      </div>

      {/* URL pill */}
      <div className="rounded-full border border-brand/15 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300 tracking-tight">
        youwont.xyz/u/scan-me
      </div>
    </div>

    {/* Live badge */}
    <div className="absolute -top-3 -right-2 flex items-center gap-1.5 rounded-full bg-slate-950 dark:bg-white px-3 py-1.5 text-[11px] font-bold text-white dark:text-slate-900 shadow-xl">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      Live
    </div>

    {/* Scan count badge */}
    <div className="absolute -bottom-4 -left-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-4 py-2 shadow-lift">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-semibold">Scans</p>
      <p className="text-lg font-black text-slate-900 dark:text-slate-100 tabular-nums" style={{ fontFamily: 'Syne, sans-serif' }}>2,847</p>
    </div>

    {/* Update badge */}
    <div className="absolute top-8 -left-5 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-3 py-1.5 shadow-soft flex items-center gap-1.5">
      <RefreshCw size={10} className="text-brand dark:text-blue-400" />
      <span className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">Updated 2m ago</span>
    </div>
  </div>
)

const features = [
  {
    num: '01',
    icon: <Zap size={20} />,
    iconBg: 'bg-blue-50 dark:bg-blue-950/50 text-brand dark:text-blue-400',
    title: 'Say Whatever You Want',
    description: "Write something clever. Something cryptic. Something that'll make people regret not scanning sooner.",
  },
  {
    num: '02',
    icon: <Image size={20} />,
    iconBg: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400',
    title: 'Worth 1,000 Words',
    description: "Drop an image. A meme. A selfie from 2007. Whatever makes people do a double-take.",
  },
  {
    num: '03',
    icon: <Shield size={20} />,
    iconBg: 'bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400',
    title: 'Mission Control',
    description: 'All your codes in one place. Update them, track who scanned, and feel like a spy doing it.',
  },
]

const Home = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showAccessForm, setShowAccessForm] = useState(false)
  const [accountForm, setAccountForm] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const accountLoggedIn = isAccountLoggedIn()

  useEffect(() => {
    if (searchParams.get('action') === 'login') setShowAccessForm(true)
  }, [searchParams])

  const handleAccountLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!accountForm.email.trim() || !accountForm.password.trim()) return
    setLoading(true)
    setError(null)
    try {
      await accountSignIn(accountForm.email, accountForm.password)
      navigate('/my-codes')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>YOWO — One QR code. Infinite reasons to be mysterious.</title>
        <meta name="description" content="Create a QR code with a message only the brave (or bored) will scan. Update it anytime. Free forever." />
      </Helmet>

      <div className="relative z-[1] min-h-[80vh] py-12 sm:py-18 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* ── Hero ── */}
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20 mb-20 sm:mb-28">

            {/* Left */}
            <div className="flex-1 text-center lg:text-left animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand/8 dark:bg-brand/15 border border-brand/12 dark:border-brand/20 text-brand dark:text-blue-400 text-sm font-semibold mb-7 tracking-tight">
                <QrCode size={13} />
                The QR code that keeps on giving
              </div>
              <h1
                className="text-5xl sm:text-6xl lg:text-7xl font-black text-slate-900 dark:text-slate-100 mb-6 leading-[1.05]"
                style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-0.03em' }}
              >
                Create a code.{' '}
                <span className="brand-text-gradient block sm:inline">
                  Make 'em curious.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-500 dark:text-slate-400 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                One QR code. Change the message anytime. Make people scan it over and over just to see what's new.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button variant="primary" size="lg" onClick={() => navigate('/setup')} className="group">
                  Create Your Code
                  <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
                {accountLoggedIn ? (
                  <Button variant="secondary" size="lg" onClick={() => navigate('/my-codes')}>
                    My Codes
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                ) : (
                  <Button variant="secondary" size="lg" onClick={() => setShowAccessForm(true)}>
                    Log In
                  </Button>
                )}
              </div>
              {!accountLoggedIn && (
                <p className="mt-4 text-sm text-slate-400 dark:text-slate-500">
                  New here?{' '}
                  <button
                    onClick={() => navigate('/account/signup')}
                    className="text-brand hover:text-brand-600 underline underline-offset-2 dark:text-blue-400 font-medium"
                  >
                    It's free. Obviously.
                  </button>
                </p>
              )}
            </div>

            {/* Right — QR mockup */}
            <div className="flex-shrink-0 flex items-center justify-center" style={{ animationDelay: '100ms' }}>
              <QrMockup />
            </div>
          </div>

          {/* ── Login form ── */}
          {showAccessForm && (
            <Card className="brand-panel max-w-md mx-auto mb-20 animate-slide-up">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-1">Welcome Back</h2>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Your codes missed you. Probably.</p>
              </div>
              <form onSubmit={handleAccountLogin} className="space-y-4">
                <Input label="Email" type="email" placeholder="you@example.com"
                  value={accountForm.email} onChange={(e) => setAccountForm(p => ({ ...p, email: e.target.value }))} required />
                <Input label="Password" type="password" placeholder="Your password"
                  value={accountForm.password} onChange={(e) => setAccountForm(p => ({ ...p, password: e.target.value }))} required />
                {error && <Alert variant="danger">{error}</Alert>}
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={() => setShowAccessForm(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" variant="primary" disabled={loading} className="flex-1">{loading ? 'Signing In...' : 'Sign In'}</Button>
                </div>
              </form>
              <div className="text-center mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  New here?{' '}
                  <button onClick={() => { setShowAccessForm(false); navigate('/account/signup') }}
                    className="text-brand hover:text-brand-600 underline underline-offset-2 dark:text-blue-400">Join the club</button>
                  {' · '}
                  <button onClick={() => { setShowAccessForm(false); navigate('/account/login') }}
                    className="text-brand hover:text-brand-600 underline underline-offset-2 dark:text-blue-400">Forgot password?</button>
                </p>
              </div>
            </Card>
          )}

          {/* ── Feature cards — numbered, staggered ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 items-start">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="animate-fade-in"
                style={{
                  animationDelay: `${i * 90}ms`,
                  marginTop: i === 1 ? '24px' : i === 2 ? '12px' : '0',
                }}
              >
                <Card className="brand-panel group hover:shadow-lift hover:-translate-y-1 transition-all duration-300 h-full relative overflow-hidden">
                  {/* Big background number */}
                  <span
                    className="absolute -top-2 right-3 text-[80px] font-black leading-none select-none text-slate-100 dark:text-slate-800 transition-all duration-300 group-hover:text-slate-200/80 dark:group-hover:text-slate-700"
                    style={{ fontFamily: 'Syne, sans-serif' }}
                  >
                    {f.num}
                  </span>
                  {/* Content */}
                  <div className="relative">
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${f.iconBg} mb-4`}>
                      {f.icon}
                    </div>
                    <h3
                      className="text-base font-bold text-slate-900 dark:text-slate-100 mb-2 leading-snug"
                      style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-0.01em' }}
                    >
                      {f.title}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">{f.description}</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>

          {/* ── Trust line ── */}
          <div className="mt-16 pt-12 border-t border-slate-200/60 dark:border-slate-800/80 text-center">
            <div className="flex flex-wrap items-center justify-center gap-3">
              {['No subscription', 'No nonsense', 'No pants required'].map((item) => (
                <span
                  key={item}
                  className="px-5 py-2 rounded-full bg-white/70 dark:bg-slate-800/70 border border-slate-200/60 dark:border-slate-700/60 text-sm font-medium text-slate-600 dark:text-slate-300 shadow-soft"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  )
}

export default Home
